import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import Stack from "@mui/material/Stack"
import { zodResolver } from "@hookform/resolvers/zod"
import { changePasswordSchema } from "validations/auth"
import { CONFIRM_NEW_PASSWORD, CURRENT_PASSWORD, MINIMUM_PASSWORD_LENGTH, NEW_PASSWORD } from "constants/index"
import { MIN_LENGTH_ZOD_ERROR } from "constants/errors"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { useChangePasswordMutation } from "appRedux/slices/authApiSlice"
import VisibleIcon from "components/atoms/_icons/VisibleIcon"
import InvisibleIcon from "components/atoms/_icons/InvisibleIcon"
import CTA from "components/atoms/CTA"
import TextField from "components/atoms/TextField"
import BottomSheet from "components/atoms/BottomSheet"
import ForgetPasswordQuestion from "components/molecules/ForgetPasswordQuestion"

type SendOTPBottomSheetProps = {
  show: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const formInitialValue = {
  [CURRENT_PASSWORD]: "",
  [NEW_PASSWORD]: "",
  [CONFIRM_NEW_PASSWORD]: ""
}

export default function ChangePasswordBottomSheet(props: SendOTPBottomSheetProps) {
  const { show, onOpen, onClose } = props

  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [triggerChangePassword, changePasswordResult] = useChangePasswordMutation()

  const {
    register,
    getValues,
    setValue,
    reset,
    handleSubmit,
    formState: { errors, dirtyFields }
  } = useForm<ChangePasswordFormFields>({
    resolver: zodResolver(changePasswordSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: formInitialValue
  })

  useEffect(() => {
    if (!show) return
    resetBottomSheetStates()
  }, [show])

  useEffect(() => {
    if (!Object.keys(errors).length) return
    console.log(errors)
    handleErrors()
  }, [errors])

  function resetBottomSheetStates() {
    reset(formInitialValue)
    setShowPassword(false)
    setShowConfirmPassword(false)
    setShowCurrentPassword(false)
  }

  function handleErrors() {
    if (errors[CURRENT_PASSWORD]) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: `password_${MIN_LENGTH_ZOD_ERROR}`,
        show: true,
        status: 400,
        message: t("errorMessage.minLengthError", { field: t("auth.password"), minLength: MINIMUM_PASSWORD_LENGTH })
      }))
    } else if (errors[CONFIRM_NEW_PASSWORD]) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: "confirmPassword",
        show: true,
        status: 400,
        message: "errorMessage.passwordsDontMatchException"
      }))
    } else if (errors[NEW_PASSWORD]) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: `password_${MIN_LENGTH_ZOD_ERROR}`,
        show: true,
        status: 400,
        message: t("errorMessage.minLengthError", { field: t("auth.password"), minLength: MINIMUM_PASSWORD_LENGTH })
      }))
    }
  }

  function handleChangePassword(values: ChangePasswordFormFields) {
    triggerChangePassword({
      currentPassword: values[CURRENT_PASSWORD],
      newPassword: values[NEW_PASSWORD],
      confirmNewPassword: values[CONFIRM_NEW_PASSWORD]
    })
  }

  function togglePasswordVisibility(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setShowPassword(!showPassword)
  }

  function toggleConfirmPasswordVisibility(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setShowConfirmPassword(!showConfirmPassword)
  }

  function toggleCurrentPasswordVisibility(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setShowCurrentPassword(!showCurrentPassword)
  }

  return (
    <BottomSheet
      id="change-password-bottom-sheet"
      title={t("auth.changePassword")}
      show={show}
      openBottomSheet={onOpen}
      closeBottomSheet={onClose}
    >
      <form onSubmit={handleSubmit(handleChangePassword)} style={{ width: "100%", marginTop: "40px" }}>
        <Stack marginTop={4}>
          <TextField
            id={CURRENT_PASSWORD}
            label={t("auth.currentPassword")}
            type={showCurrentPassword ? "text" : "password"}
            required={true}
            defaultValue={getValues(CURRENT_PASSWORD)}
            startAdornmentIcon={showCurrentPassword ? <VisibleIcon/> : <InvisibleIcon/>}
            onIconClick={toggleCurrentPasswordVisibility}
            register={register}
            setValue={setValue}
            error={errors[CURRENT_PASSWORD]}
            direction={"rtl"}
          />
          <TextField
            id={NEW_PASSWORD}
            label={t("auth.newPassword")}
            type={showPassword ? "text" : "password"}
            required={true}
            defaultValue={getValues(NEW_PASSWORD)}
            startAdornmentIcon={showPassword ? <VisibleIcon/> : <InvisibleIcon/>}
            onIconClick={togglePasswordVisibility}
            register={register}
            setValue={setValue}
            error={errors[CURRENT_PASSWORD] ? undefined : errors[NEW_PASSWORD]}
            direction={"rtl"}
          />
          <TextField
            id={CONFIRM_NEW_PASSWORD}
            label={t("auth.confirmNewPassword")}
            type={showConfirmPassword ? "text" : "password"}
            required={true}
            defaultValue={getValues(CONFIRM_NEW_PASSWORD)}
            startAdornmentIcon={showConfirmPassword ? <VisibleIcon/> : <InvisibleIcon/>}
            onIconClick={toggleConfirmPasswordVisibility}
            register={register}
            setValue={setValue}
            error={errors[NEW_PASSWORD] ? undefined : errors[CONFIRM_NEW_PASSWORD]}
            direction={"rtl"}
          />
          <ForgetPasswordQuestion/>
          <CTA
            variant="contained"
            type="submit"
            disabled={Object.keys(dirtyFields).length !== 3 || changePasswordResult.isLoading}
            loading={changePasswordResult.isLoading}
            onClick={handleSubmit(handleChangePassword)}
          >
            {t("general.submit")}
          </CTA>
        </Stack>
      </form>
    </BottomSheet>
  )
}
